import { isUsableRate } from '@/shared/lib/format-money';
import type { CommercialFxRate, FxRate } from './api';

/** Rates the UI can show, by currency code; missing, zero or unparsable ones are dropped. */
export function usableRatesByCode<T extends { code: string; rate_to_rub: string }>(
  rates: readonly T[] | undefined,
): Map<string, T> {
  return new Map((rates ?? []).filter((r) => isUsableRate(r.rate_to_rub)).map((r) => [r.code, r]));
}

/** "2026-09-25" -> "25.09.2026" (the CBR rate date as Russian readers expect it). */
export function formatRateDate(isoDate: string): string {
  const [y, m, d] = isoDate.split('-');
  return `${d}.${m}.${y}`;
}

/** Newest as_of among the rates, or null when there are none. */
export function latestAsOf(rates: Iterable<FxRate>): string | null {
  let latest: string | null = null;
  for (const r of rates) {
    if (latest === null || r.as_of > latest) latest = r.as_of;
  }
  return latest;
}

/** A CBR rate dated more than this many calendar days before today is flagged as outdated. */
const OUTDATED_AFTER_DAYS = 5;
const DAY_MS = 86_400_000;

function isOutdated(asOf: string | null, today: Date): boolean {
  if (!asOf) return false;
  const [y = 0, m = 1, d = 1] = asOf.split('-').map(Number);
  const rateDay = Date.UTC(y, m - 1, d);
  const todayDay = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return (todayDay - rateDay) / DAY_MS > OUTDATED_AFTER_DAYS;
}

/** "3.50" -> "3,5"; "-2.00" -> "-2". */
function markupText(markupPct: string | null): string | null {
  const n = Number(markupPct);
  if (markupPct === null || !Number.isFinite(n) || n === 0) return null;
  return String(n).replace('.', ',');
}

/** Tooltip of a ticker item: where the rate comes from, and a flag when its CBR date is old. */
export function tickerItemTitle(rate: CommercialFxRate, today: Date = new Date()): string {
  if (rate.source === 'org_custom') return 'Коммерческий курс: курс компании';
  const date = rate.as_of ? ` на ${formatRateDate(rate.as_of)}` : '';
  const outdated = isOutdated(rate.as_of, today) ? ' (курс устарел)' : '';
  if (rate.source === 'cbr') return `Курс ЦБ РФ${date}${outdated}`;
  const markup = markupText(rate.markup_pct);
  const markupPart = markup ? ` + наценка ${markup} %` : ', без наценки';
  return `Коммерческий курс: курс ЦБ РФ${date}${markupPart}${outdated}`;
}
